import React, { useState, useEffect } from 'react';
import { getLoginId } from '../Authentication/AuthSvc/authservice';
import { Bell, Check, X, Clock, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import './inboxcontent.css';

const InboxContent = () => {
  const [invitations, setInvitations] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loginId = getLoginId();
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    Promise.all([
      fetchInvitations(),
      fetchUnreadMessages()
    ]).finally(() => setLoading(false));
  }, []);

  const fetchInvitations = async () => {
    try {
      const response = await fetch(`${API_URL}/user/${loginId}/invitations`);
      if (!response.ok) throw new Error('Failed to fetch invitations');
      const data = await response.json();
      console.log('Raw invitation data:', data); // Debug log to see actual structure
      setInvitations(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load invitations');
      setLoading(false);
    }
  };

  // Helper function to safely get inviter name from potential data structures
  const getInviterName = (invitation) => {
    // Check various possible structures based on database response
    if (!invitation) return 'Unknown';
    
    // Check if inviter exists as an array
    if (invitation.inviter && Array.isArray(invitation.inviter) && invitation.inviter.length > 0) {
      return `${invitation.inviter[0].first_name || ''} ${invitation.inviter[0].last_name || ''}`.trim() || 'Unknown';
    }
    
    // Check if inviter exists as an object with first_name/last_name properties
    if (invitation.inviter && typeof invitation.inviter === 'object' && (invitation.inviter.first_name || invitation.inviter.last_name)) {
      return `${invitation.inviter.first_name || ''} ${invitation.inviter.last_name || ''}`.trim() || 'Unknown';
    }
    
    // Check if invited_by exists directly
    if (invitation.invited_by) {
      // If it's just a user ID, return that with a prefix
      if (typeof invitation.invited_by === 'string' || typeof invitation.invited_by === 'number') {
        return `User ${invitation.invited_by}`;
      }
    }
    
    return 'Unknown';
  };

  // Helper function for project title
  const getProjectTitle = (invitation) => {
    if (!invitation) return 'Untitled Project';
    
    // Check if project exists as an array
    if (invitation.project && Array.isArray(invitation.project) && invitation.project.length > 0) {
      return invitation.project[0].title || 'Untitled Project';
    }
    
    // Check if project exists as an object with title property
    if (invitation.project && typeof invitation.project === 'object' && invitation.project.title) {
      return invitation.project.title;
    }
    
    // Check for direct properties
    if (invitation.title) {
      return invitation.title;
    }
    
    return 'Untitled Project';
  };

  // Helper function for project details
  const getProjectDetails = (invitation) => {
    if (!invitation) return 'No details available';
    
    // Check if project exists as an array
    if (invitation.project && Array.isArray(invitation.project) && invitation.project.length > 0) {
      return invitation.project[0].details || 'No details available';
    }
    
    // Check if project exists as an object with details property
    if (invitation.project && typeof invitation.project === 'object' && invitation.project.details) {
      return invitation.project.details;
    }
    
    // Check for direct properties
    if (invitation.details) {
      return invitation.details;
    }
    
    return 'No details available';
  };

const handleResponse = async (invitationId, status) => { // Handle response to invitation
  const confirmation = window.confirm(`Are you sure you want to ${status} this invitation?`);
  if (!confirmation) return;
    try {
      const response = await fetch(`${API_URL}/invitation/${invitationId}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) throw new Error('Failed to respond to invitation');
      
      // Refresh invitations after response
      fetchInvitations();
    } catch (err) {
      setError('Failed to respond to invitation');
    }
  };

  const fetchUnreadMessages = async () => {
    try {
      // Fetch only direct messages first for testing
      const response = await fetch(`${API_URL}/api/chat/rooms?userId=${loginId}&chatType=direct`);
      
      if (!response.ok) {
        console.error('Response not OK:', await response.text());
        throw new Error('Failed to fetch messages');
      }
  
      const rooms = await response.json();
  
      // Filter rooms with unread messages
      const unread = rooms.filter(room => {
        const lastReadTime = localStorage.getItem(`lastRead_${room.chat_room_id}`);
        const hasUnread = room.last_message && (!lastReadTime || new Date(room.last_activity) > new Date(lastReadTime));
        return hasUnread;
      });
      
      setUnreadMessages(unread);
    } catch (err) {
      console.error('Error in fetchUnreadMessages:', err);
      setError('Failed to load messages');
    }
  };
  
  const handleMessageAction = (chatRoomId, action, chatType) => {
    if (action === 'view') {
      // Store current timestamp as last read time
      localStorage.setItem(`lastRead_${chatRoomId}`, new Date().toISOString());
      // Navigate to chat interface
      if (chatType === 'project') {
        navigate(`/chat/project/${chatRoomId}`);
      } else {
        navigate(`/chat?room=${chatRoomId}`);
      }
    } else if (action === 'ignore') {
      // Mark as read without viewing
      localStorage.setItem(`lastRead_${chatRoomId}`, new Date().toISOString());
      setUnreadMessages(prev => prev.filter(msg => msg.chat_room_id !== chatRoomId));
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Clock className="animate-spin h-8 w-8 text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 p-4 text-center">
        {error}
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-4xl" id="inboxPage">
      <div className="flex items-center gap-2 mb-6">
        <Bell className="h-6 w-6 text-blue-500" />
        <h1 className="text-2xl font-semibold">Notifications</h1>
      </div>

      {/* Unread Messages Section */}
      {unreadMessages.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-blue-500" />
            Unread Messages
          </h2>
          <div className="space-y-4">
            {unreadMessages.map((msg) => (
              <div key={msg.chat_room_id} 
                className="border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-medium">{msg.name}</h3>
                    <p className="text-gray-600 text-sm">{msg.last_message}</p>
                  </div>
                  <span className="text-sm text-gray-500">
                    {new Date(msg.last_activity).toLocaleDateString()}
                  </span>
                </div>
                <div className="button area">
                <button
                  onClick={() => handleMessageAction(msg.chat_room_id, 'ignore', msg.chat_type)}
                  className="message ignore button"
                >
                  Ignore
                </button>
                <button
                  onClick={() => handleMessageAction(msg.chat_room_id, 'view', msg.chat_type)}
                  className="message view button"
                >
                  View Chat
                </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="inboxHeader">
        <Bell className="h-6 w-6 text-blue-500" />
        <h1 className="inboxTitle">Project Invitations</h1>
      </div>

      {invitations.length === 0 ? (
        <div className="text-center p-6 bg-gray-50 rounded-lg">
          <p className="inboxText">No pending invitations</p>
        </div>
      ) : (
        <div className="space-y-4">
          {invitations.map((invitation) => (
            <div 
              key={invitation.invitation_id} 
              className="border rounded-lg p-6 bg-white shadow-sm"
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-semibold">
                    {getProjectTitle(invitation)}
                  </h2>
                  <p className="inboxText">
                    Invited by {getInviterName(invitation)}
                  </p>
                </div>
                <div className="inboxText">
                  {new Date(invitation.invited_at).toLocaleDateString()}
                </div>
              </div>
              
              {/* Content */}
              <div className="space-y-2 mb-4">
                <p className="inboxText">
                  {getProjectDetails(invitation)}
                </p>
                <p className='inboxHeading'>Role: </p>
                <div className="inboxText">
                  {invitation.role || 'Not specified'}
                </div>
              </div>

              {/* Actions */}
              <div className="inboxActions">
                <button
                  onClick={() => handleResponse(invitation.invitation_id, 'declined')}
                  className="inboxBtnrej"
                >
                  <X className="icon" />
                  Decline
                </button>
                <button
                  onClick={() => handleResponse(invitation.invitation_id, 'accepted')}
                  className="inboxBtn"
                >
                  <Check className="icon" />
                  Accept
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InboxContent;
